import { useState, useEffect } from 'react'
import { Bell, BellRing } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import athleteApi from '@/lib/api'
import { useNavigate } from 'react-router-dom'
import { usePushNotifications } from '@/hooks/usePushNotifications'

interface Notification {
  id: number
  title: string
  body: string | null
  collaboration_id: string | null
  is_read: boolean
  created_at: string
  notification_type: string
  metadata?: {
    wave_id?: string
    milestone_name?: string
  }
  link_url?: string | null
}

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { permissionState, isSubscribing, subscribe } = usePushNotifications()

  const fetchNotifications = async () => {
    try {
      const response = await athleteApi.get('/notifications')
      setNotifications(response.data.notifications)
      setUnreadCount(response.data.unreadCount)
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    }
  }

  useEffect(() => {
    fetchNotifications()
    // Poll for new notifications every 180 seconds
    const interval = setInterval(fetchNotifications, 180000)
    return () => clearInterval(interval)
  }, [])

  const getNotificationUrl = (notification: Notification): string | null => {
    // Construct URL based on notification type and collaboration_id (already encoded)
    if (notification.link_url) {
      return notification.link_url
    }

    if (!notification.collaboration_id) {
      return null
    }

    // Negotiation messages should go to the bids page (before offer acceptance)
    if (
      notification.notification_type === 'negotiation_message' ||
      notification.notification_type === 'offer_terms_updated'
    ) {
      return `/bids/${notification.collaboration_id}`
    }

    // All other notifications (after acceptance) go to the campaign detail page
    // This includes: submission_approved, submission_revision_requested, offer_accepted, etc.
    return `/collaborations/${notification.collaboration_id}`
  }

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await athleteApi.patch(`/notifications/${notification.id}/read`)
        setUnreadCount((prev) => Math.max(0, prev - 1))
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)),
        )
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    }

    // Navigate to constructed URL
    const url = getNotificationUrl(notification)
    if (url) {
      navigate(url)
    }

    setOpen(false)
  }

  const markAllAsRead = async () => {
    try {
      await athleteApi.post('/notifications/mark-all-read')
      setUnreadCount(0)
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
    return `${Math.floor(diffMins / 1440)}d ago`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-7 w-7" style={{ color: '#C0FD72' }} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 bg-[#1a1a1a] border-white/20" align="end">
        <div className="flex items-center justify-between border-b border-white/20 p-3">
          <h3 className="font-semibold text-white">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs h-8 text-gray-300 hover:text-white hover:bg-white/10"
            >
              Mark all read
            </Button>
          )}
        </div>

        {permissionState === 'default' && (
          <div className="flex items-center gap-3 px-3 py-2.5 border-b border-white/10 bg-[#C0FD72]/5">
            <BellRing className="h-4 w-4 text-[#C0FD72] shrink-0" />
            <p className="text-xs text-gray-300 flex-1">
              Get notified when you receive new offers
            </p>
            <Button
              size="sm"
              onClick={subscribe}
              disabled={isSubscribing}
              variant="lime"
              className="h-7 text-xs shrink-0"
            >
              {isSubscribing ? 'Enabling…' : 'Enable'}
            </Button>
          </div>
        )}

        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No notifications yet</div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`
                  p-3 border-b border-white/10 cursor-pointer hover:bg-white/5 transition-colors
                  ${!notification.is_read ? 'bg-white/10' : ''}
                `}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm text-white ${!notification.is_read ? 'font-semibold' : ''}`}
                      >
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <div className="h-2 w-2 rounded-full bg-[#C0FD72] mt-1 shrink-0" />
                      )}
                    </div>
                    {notification.body && (
                      <p className="text-xs text-gray-300 mt-1">{notification.body}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
